import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useCallback, useState } from 'react';
import { createChart, ColorType, CrosshairMode, LineStyle, } from 'lightweight-charts';
import { formatPrice } from '@/lib/smc';
import { BarChart2, Layers, TrendingUp, TrendingDown, Clock } from 'lucide-react';
import { clsx } from 'clsx';
const TIMEFRAMES = ['M5', 'M15', 'M30', 'H1', 'H4', 'D1'];
const TF_POLL_LABEL = {
    M5: '~30s', M15: '~1m', M30: '~2m', H1: '~5m', H4: '~10m', D1: '~30m',
};
const DARK_COLORS = { bg: '#0d1117', grid: '#1a2030', text: '#6b7280', border: '#1f2937' };
const LIGHT_COLORS = { bg: '#f0f4f8', grid: '#dde3ec', text: '#64748b', border: '#cbd5e1' };
const CHART_COLORS = {
    bull: '#10b981', bear: '#ef4444',
    wick_bull: '#10b981', wick_bear: '#ef4444',
    ob_bull: 'rgba(16,185,129,0.18)', ob_bear: 'rgba(239,68,68,0.18)',
    fvg_bull: 'rgba(99,102,241,0.18)', fvg_bear: 'rgba(245,158,11,0.18)',
    entry: '#6366f1', sl: '#ef4444', tp: '#10b981',
};
export function TradingChart({ signal, timeframe, onTimeframeChange, isDark }) {
    const themeColors = isDark ? DARK_COLORS : LIGHT_COLORS;
    const containerRef = useRef(null);
    const overlayRef = useRef(null);
    const chartRef = useRef(null);
    const candleRef = useRef(null);
    const resizeObRef = useRef(null);
    const [tab, setTab] = useState('chart');
    // ── Draw OB + FVG overlays on canvas ──────────────────────────────────────
    const redrawOverlay = useCallback(() => {
        const canvas = overlayRef.current;
        const chart = chartRef.current;
        if (!canvas || !chart || !signal)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx)
            return;
        const W = canvas.width;
        const H = canvas.height;
        ctx.clearRect(0, 0, W, H);
        const series = candleRef.current;
        const toY = (price) => series?.priceToCoordinate(price) ?? null;
        const toX = (t) => chart.timeScale().timeToCoordinate(t);
        const drawRect = (x1, y1, x2, y2, fillColor, strokeColor, label, labelColor, dashed = false) => {
            if (x1 === null || y1 === null || x2 === null || y2 === null)
                return;
            const lx = Math.max(0, x1);
            const rx = Math.min(W, x2);
            const ty = Math.min(y1, y2);
            const by = Math.max(y1, y2);
            const h = by - ty;
            if (h < 1 || rx - lx < 1)
                return;
            ctx.globalAlpha = 0.85;
            ctx.fillStyle = fillColor;
            ctx.fillRect(lx, ty, rx - lx, h);
            ctx.globalAlpha = 1;
            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = 1;
            if (dashed)
                ctx.setLineDash([4, 3]);
            else
                ctx.setLineDash([]);
            ctx.strokeRect(lx, ty, rx - lx, h);
            ctx.setLineDash([]);
            // Label
            ctx.font = '9px "JetBrains Mono", monospace';
            ctx.fillStyle = labelColor;
            ctx.fillText(label, lx + 4, ty + 11);
            ctx.globalAlpha = 1;
        };
        // ── Order Blocks ──────────────────────────────────────────────────────
        for (const ob of signal.orderBlocks) {
            const x1 = toX(ob.timeStart);
            const x2 = W;
            const y1 = toY(ob.top);
            const y2 = toY(ob.bottom);
            const isBull = ob.type === 'bullish';
            const alpha = ob.mitigated ? 0.5 : 1;
            ctx.globalAlpha = alpha;
            drawRect(x1, y1, x2, y2, isBull ? CHART_COLORS.ob_bull : CHART_COLORS.ob_bear, isBull ? CHART_COLORS.bull : CHART_COLORS.bear, `OB ${ob.strength}%${ob.mitigated ? ' (Mit.)' : ''}`, isBull ? CHART_COLORS.bull : CHART_COLORS.bear);
        }
        // ── Fair Value Gaps ───────────────────────────────────────────────────
        for (const fvg of signal.fvgs) {
            const x1 = toX(fvg.time);
            const x2 = W;
            const y1 = toY(fvg.top);
            const y2 = toY(fvg.bottom);
            const isBull = fvg.type === 'bullish';
            ctx.globalAlpha = fvg.filled ? 0.35 : 0.85;
            drawRect(x1, y1, x2, y2, isBull ? CHART_COLORS.fvg_bull : CHART_COLORS.fvg_bear, isBull ? '#6366f1' : '#f59e0b', `FVG${fvg.filled ? ` ${fvg.percentageFilled}%` : ''}`, isBull ? '#818cf8' : '#fbbf24', true);
        }
        ctx.globalAlpha = 1;
        // ── Entry / SL / TP horizontal lines ─────────────────────────────────
        const drawHLine = (price, color, label, dash) => {
            const y = toY(price);
            if (y === null)
                return;
            ctx.strokeStyle = color;
            ctx.lineWidth = 1;
            ctx.setLineDash(dash);
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(W, y);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.font = '9px "JetBrains Mono", monospace';
            ctx.fillStyle = color;
            ctx.fillText(label, 4, y - 3);
        };
        if (signal) {
            drawHLine(signal.entry, CHART_COLORS.entry, `ENTRY ${formatPrice(signal.entry, signal.pair)}`, [6, 3]);
            drawHLine(signal.sl, CHART_COLORS.sl, `SL ${formatPrice(signal.sl, signal.pair)}`, [3, 3]);
            drawHLine(signal.tp1, CHART_COLORS.tp, `TP1 ${formatPrice(signal.tp1, signal.pair)}`, [4, 4]);
            drawHLine(signal.tp2, CHART_COLORS.tp, `TP2 ${formatPrice(signal.tp2, signal.pair)}`, [4, 4]);
            drawHLine(signal.tp3, CHART_COLORS.tp, `TP3 ${formatPrice(signal.tp3, signal.pair)}`, [4, 4]);
        }
        // ── Liquidity sweep markers ───────────────────────────────────────────
        for (const sweep of signal.liquiditySweeps) {
            const y = toY(sweep.sweptAt);
            const x = toX(sweep.time);
            if (y === null || x === null)
                continue;
            ctx.fillStyle = '#f59e0b';
            ctx.strokeStyle = '#f59e0b';
            ctx.lineWidth = 1;
            ctx.setLineDash([2, 2]);
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(W, y);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.font = '9px monospace';
            ctx.fillText(`⚡ Sweep ${sweep.type === 'buy_side' ? 'BSL' : 'SSL'}`, x + 4, y - 3);
        }
    }, [signal]);
    // ── Initialise / update chart ─────────────────────────────────────────────
    useEffect(() => {
        const container = containerRef.current;
        if (!container)
            return;
        const chart = createChart(container, {
            layout: {
                background: { type: ColorType.Solid, color: themeColors.bg },
                textColor: themeColors.text,
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: 11,
            },
            grid: {
                vertLines: { color: themeColors.grid, style: LineStyle.Dashed },
                horzLines: { color: themeColors.grid, style: LineStyle.Dashed },
            },
            crosshair: { mode: CrosshairMode.Normal },
            rightPriceScale: { borderColor: themeColors.border },
            timeScale: {
                borderColor: themeColors.border,
                timeVisible: true,
                secondsVisible: false,
                fixLeftEdge: true,
            },
            handleScroll: true,
            handleScale: true,
        });
        chartRef.current = chart;
        const series = chart.addCandlestickSeries({
            upColor: CHART_COLORS.bull,
            downColor: CHART_COLORS.bear,
            borderVisible: false,
            wickUpColor: CHART_COLORS.wick_bull,
            wickDownColor: CHART_COLORS.wick_bear,
        });
        candleRef.current = series;
        // Sync overlay canvas size with chart container
        const syncOverlay = () => {
            const overlay = overlayRef.current;
            if (!overlay)
                return;
            overlay.width = container.clientWidth;
            overlay.height = container.clientHeight;
            redrawOverlay();
        };
        chart.timeScale().subscribeVisibleTimeRangeChange(() => redrawOverlay());
        chart.subscribeCrosshairMove(() => redrawOverlay());
        resizeObRef.current = new ResizeObserver(() => {
            chart.applyOptions({ width: container.clientWidth, height: container.clientHeight });
            syncOverlay();
        });
        resizeObRef.current.observe(container);
        syncOverlay();
        return () => {
            resizeObRef.current?.disconnect();
            chart.remove();
            chartRef.current = null;
            candleRef.current = null;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // ── Re-theme chart when isDark changes ───────────────────────────────────
    useEffect(() => {
        const chart = chartRef.current;
        if (!chart)
            return;
        const tc = isDark ? DARK_COLORS : LIGHT_COLORS;
        chart.applyOptions({
            layout: { background: { type: ColorType.Solid, color: tc.bg }, textColor: tc.text },
            grid: { vertLines: { color: tc.grid }, horzLines: { color: tc.grid } },
            rightPriceScale: { borderColor: tc.border },
            timeScale: { borderColor: tc.border },
        });
        requestAnimationFrame(() => redrawOverlay());
    }, [isDark, redrawOverlay]);
    // ── Load candle data when signal changes ──────────────────────────────────
    useEffect(() => {
        const series = candleRef.current;
        const chart = chartRef.current;
        if (!series || !signal)
            return;
        const data = signal.candles.map(c => ({
            time: c.time,
            open: c.open,
            high: c.high,
            low: c.low,
            close: c.close,
        }));
        series.setData(data);
        chart?.timeScale().fitContent();
        // Redraw overlay after data + layout settle
        requestAnimationFrame(() => redrawOverlay());
    }, [signal, redrawOverlay]);
    const isBull = signal?.direction === 'LONG';
    return (_jsxs("div", { className: "flex flex-col h-full min-h-0", children: [_jsxs("div", { className: "flex items-center justify-between px-4 py-2.5 border-b border-theme bg-surface flex-shrink-0", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(BarChart2, { size: 14, className: "text-indigo-400" }), signal ? (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm font-mono font-semibold text-white", children: signal.pair }), _jsxs("span", { className: clsx('badge text-[10px]', isBull ? 'badge-bull' : 'badge-bear'), children: [isBull ? _jsx(TrendingUp, { size: 9 }) : _jsx(TrendingDown, { size: 9 }), signal.direction] }), _jsx("span", { className: "badge badge-muted text-[10px]", children: timeframe }), _jsxs("span", { className: "flex items-center gap-1 text-[9px] font-mono text-gray-600", children: [_jsx(Clock, { size: 9 }), TF_POLL_LABEL[timeframe]] })] })) : (_jsx("span", { className: "text-sm text-gray-500", children: "Select a signal" }))] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "flex items-center gap-1 bg-white/[0.03] border border-white/[0.08] rounded-lg p-0.5", children: TIMEFRAMES.map(tf => (_jsx("button", { onClick: () => onTimeframeChange(tf), title: `Poll every ${TF_POLL_LABEL[tf]}`, className: clsx('px-2.5 py-1 rounded-md text-[10px] font-mono font-semibold transition-all', timeframe === tf
                                        ? 'bg-indigo-600 text-white shadow-[0_0_8px_rgba(99,102,241,0.4)]'
                                        : 'text-gray-500 hover:text-gray-300'), children: tf }, tf))) }), _jsx("div", { className: "flex items-center gap-1", children: ['chart', 'analysis'].map(t => (_jsx("button", { onClick: () => setTab(t), className: clsx('px-3 py-1 rounded-md text-[11px] font-medium transition-colors capitalize', tab === t
                                        ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                        : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'), children: t }, t))) })] })] }), signal && tab === 'chart' && (_jsxs("div", { className: "flex items-center gap-4 px-4 py-1.5 border-b border-theme text-[10px] font-mono flex-shrink-0 bg-surface", children: [_jsx(LegendItem, { color: "#10b981", label: "OB Bullish", dash: false }), _jsx(LegendItem, { color: "#ef4444", label: "OB Bearish", dash: false }), _jsx(LegendItem, { color: "#6366f1", label: "FVG Bull", dash: true }), _jsx(LegendItem, { color: "#f59e0b", label: "FVG Bear", dash: true }), _jsx(LegendItem, { color: "#f59e0b", label: "\u26A1 Sweep", dash: true }), _jsxs("div", { className: "ml-auto flex items-center gap-1 text-gray-600", children: [_jsx(Layers, { size: 10 }), _jsxs("span", { children: [signal.orderBlocks.length, " OB \u00B7 ", signal.fvgs.length, " FVG"] })] })] })), tab === 'chart' ? (_jsxs("div", { className: "flex-1 relative min-h-0 tv-chart-wrapper", children: [_jsx("div", { ref: containerRef, className: "absolute inset-0" }), _jsx("canvas", { ref: overlayRef, className: "chart-overlay-canvas" }), !signal && _jsx(ChartPlaceholder, {})] })) : (signal && _jsx(AnalysisPanel, { signal: signal }))] }));
}
// ── Sub-components ────────────────────────────────────────────────────────────
function LegendItem({ color, label, dash }) {
    return (_jsxs("div", { className: "flex items-center gap-1.5 text-gray-500", children: [_jsx("span", { className: "inline-block w-6 h-2 rounded-sm flex-shrink-0", style: {
                    background: `${color}30`,
                    border: `1px ${dash ? 'dashed' : 'solid'} ${color}`,
                } }), label] }));
}
function ChartPlaceholder() {
    return (_jsxs("div", { className: "absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#0d1117]/60 backdrop-blur-sm", children: [_jsx(BarChart2, { size: 36, className: "text-gray-700" }), _jsx("p", { className: "text-sm text-gray-500 font-medium", children: "Select a signal to load chart" }), _jsx("p", { className: "text-xs text-gray-700", children: "Order blocks & FVGs will be drawn automatically" })] }));
}
function AnalysisPanel({ signal }) {
    return (_jsx("div", { className: "flex-1 overflow-y-auto p-4 min-h-0", children: _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs(Section, { title: "HTF Context", children: [_jsx(Row, { label: "Trend", value: signal.htfTrend, color: signal.htfTrend === 'BULLISH' ? 'text-emerald-400' : signal.htfTrend === 'BEARISH' ? 'text-red-400' : 'text-gray-400' }), _jsx(Row, { label: "Structure", value: signal.marketStructure, color: "text-violet-400" }), _jsx(Row, { label: "Session", value: signal.session }), _jsx(Row, { label: "RRR", value: `1:${signal.rrr.toFixed(2)}`, color: "text-emerald-400" })] }), _jsxs(Section, { title: "Volume Analysis", children: [_jsx(Row, { label: "Delta", value: `${signal.volumeAnalysis.delta > 0 ? '+' : ''}${signal.volumeAnalysis.delta.toLocaleString()}`, color: signal.volumeAnalysis.delta > 0 ? 'text-emerald-400' : 'text-red-400' }), _jsx(Row, { label: "Imbalance", value: signal.volumeAnalysis.imbalance, color: "text-gray-300" }), _jsx(Row, { label: "Rel. Volume", value: `${signal.volumeAnalysis.relativeVolume}x`, color: "text-amber-400" }), _jsx(Row, { label: "Spoofing", value: signal.volumeAnalysis.spoofingDetected ? 'DETECTED' : 'Clean', color: signal.volumeAnalysis.spoofingDetected ? 'text-red-400' : 'text-emerald-400' }), _jsx(Row, { label: "Absorption", value: signal.volumeAnalysis.absorptionDetected ? 'YES' : 'No', color: signal.volumeAnalysis.absorptionDetected ? 'text-sky-400' : 'text-gray-500' })] }), _jsx(Section, { title: "Confluence Breakdown", className: "col-span-2", children: signal.confluences.map((c, i) => (_jsxs("div", { className: "flex items-start justify-between py-2 border-b border-white/[0.04] last:border-0", children: [_jsxs("div", { className: "flex items-start gap-2", children: [_jsx("span", { className: clsx('w-2 h-2 rounded-full mt-0.5 flex-shrink-0', c.met ? 'bg-emerald-400' : 'bg-gray-600') }), _jsxs("div", { children: [_jsxs("p", { className: "text-xs font-medium text-gray-300", children: [c.name, " ", _jsxs("span", { className: "text-gray-600", children: ["(", c.weight, "%)"] })] }), _jsx("p", { className: "text-[10px] text-gray-500 mt-0.5", children: c.description })] })] }), _jsx("span", { className: clsx('badge text-[9px] ml-2 flex-shrink-0', c.met ? 'badge-bull' : 'badge-muted'), children: c.met ? '✓ Met' : '✗ Unmet' })] }, i))) }), signal.liquiditySweeps.length > 0 && (_jsx(Section, { title: "Liquidity Sweeps", className: "col-span-2", children: signal.liquiditySweeps.map((s, i) => (_jsxs("div", { className: "flex items-center justify-between py-1.5", children: [_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-amber-400 text-xs", children: "\u26A1" }), _jsx("span", { className: "text-xs text-gray-400", children: s.type === 'buy_side' ? 'Buy-Side Liquidity' : 'Sell-Side Liquidity' })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-xs font-mono text-gray-300", children: formatPrice(s.level, signal.pair) }), _jsx("span", { className: clsx('badge text-[9px]', s.confirmed ? 'badge-bull' : 'badge-muted'), children: s.confirmed ? 'Confirmed' : 'Pending' })] })] }, i))) }))] }) }));
}
function Section({ title, children, className }) {
    return (_jsxs("div", { className: clsx('glass-card p-3', className), children: [_jsx("p", { className: "text-[10px] font-mono text-gray-500 uppercase tracking-wider mb-2", children: title }), children] }));
}
function Row({ label, value, color }) {
    return (_jsxs("div", { className: "flex items-center justify-between py-1", children: [_jsx("span", { className: "text-[10px] text-gray-500", children: label }), _jsx("span", { className: clsx('text-[10px] font-mono font-medium', color ?? 'text-gray-300'), children: value })] }));
}
